// 網址觸發登入／註冊彈窗的判定與正規化（純函式，無 Nuxt runtime 依賴）
// 規格：spec/JT-1784716920_url-modal-trigger-announcement-deferral.md

export const MODAL_INTENT = {
  REGISTER: 'register',
  LOGIN: 'login',
}

// 由網址觸發、會讓公告延後的彈窗。意圖 → 彈窗元件名的唯一對應表：
// resolveModalComponent() 與 intentOfModalName() 都由此衍生，日後改名彈窗元件只需改這裡一處
const MODAL_BY_INTENT = {
  [MODAL_INTENT.LOGIN]: 'ModalPanelLogin',
  [MODAL_INTENT.REGISTER]: 'ModalPanelSignUp',
}

// 正規化後（去語系前綴、去結尾斜線、轉小寫）的觸發路徑
const PATH_INTENT_MAP = {
  '/register': MODAL_INTENT.REGISTER,
  '/login': MODAL_INTENT.LOGIN,
  '/guest/register': MODAL_INTENT.REGISTER,
  '/guest/login': MODAL_INTENT.LOGIN,
}

const GUEST_SCOPE = '/guest'

// 根路徑必須保留 '/'，其餘去掉結尾斜線以統一比對
const stripTrailingSlash = (path) => {
  const stripped = String(path || '/').replace(/\/+$/, '')
  return stripped || '/'
}

// 本專案語系碼一律為 xx-XX 形式（@nuxtjs/i18n locales 設定）
const LOCALE_SEGMENT = /^[a-z]{2}-[A-Z]{2}$/

// 只切「完整的」語系區段，避免 /registerX、/th-THX 這類同字首路徑被誤切
// locales 未提供時（例：plugin 於 $i18n 就緒前呼叫）退回 xx-XX 形式判斷
export const stripLocalePrefix = (path, locales = []) => {
  const source = String(path || '/')
  for (const locale of locales) {
    if (source === `/${locale}`) return { locale, basePath: '/' }
    if (source.startsWith(`/${locale}/`)) return { locale, basePath: source.slice(locale.length + 1) }
  }

  if (!locales.length) {
    const [, first] = source.split('/')
    if (first && LOCALE_SEGMENT.test(first)) {
      const basePath = source.slice(first.length + 1)
      return { locale: first, basePath: basePath || '/' }
    }
  }

  return { locale: null, basePath: source }
}

export const normalizeBasePath = (path, locales = []) => {
  const { basePath } = stripLocalePrefix(path, locales)
  return stripTrailingSlash(basePath).toLowerCase()
}

// 需登入頁面（/main 及其子路由）不吃 modal 參數與觸發路徑（規格第 8 節）
export const isAuthRequiredPath = (basePath) => {
  const normalized = stripTrailingSlash(basePath).toLowerCase()
  return normalized === '/main' || normalized.startsWith('/main/')
}

// 陣列型 query（?error=a&error=b）非預期輸入，一律視為無錯誤碼
export const hasErrorParam = (query = {}) => typeof query?.error === 'string' && query.error.trim() !== ''

const parseModalQuery = (value) => {
  if (typeof value !== 'string') return null
  const normalized = value.trim().toLowerCase()
  return normalized === MODAL_INTENT.REGISTER || normalized === MODAL_INTENT.LOGIN ? normalized : null
}

const NO_INTENT = { intent: null, source: null }

export const resolveModalIntent = ({ path, query = {}, locales = [] } = {}) => {
  // 錯誤參數優先：只顯示錯誤彈窗，忽略 modal 參數與路徑觸發
  if (hasErrorParam(query)) return { ...NO_INTENT }

  const basePath = normalizeBasePath(path, locales)
  if (isAuthRequiredPath(basePath)) return { ...NO_INTENT }

  // 路徑優先於參數
  const pathIntent = PATH_INTENT_MAP[basePath]
  if (pathIntent) return { intent: pathIntent, source: 'path' }

  const queryIntent = parseModalQuery(query?.modal)
  return queryIntent ? { intent: queryIntent, source: 'query' } : { ...NO_INTENT }
}

// 回傳需要導向的目標；已是正規形式（或無意圖）時回傳 null 代表不需導頁
export const buildNormalizedRoute = ({ path, query = {}, locales = [] } = {}) => {
  const { intent } = resolveModalIntent({ path, query, locales })
  if (!intent) return null

  const { locale } = stripLocalePrefix(path, locales)
  const basePath = normalizeBasePath(path, locales)
  const scope = basePath === GUEST_SCOPE || basePath.startsWith(`${GUEST_SCOPE}/`) ? GUEST_SCOPE : ''
  const targetPath = `${locale ? `/${locale}` : ''}${scope}/${intent}`

  const { modal: _modal, ...restQuery } = query || {}
  // 大小寫不符時 stripTrailingSlash(path) 與 targetPath 仍會不同，故仍會導頁修正
  if (stripTrailingSlash(path) === targetPath && !('modal' in (query || {}))) return null

  return { path: targetPath, query: restQuery }
}

// 站台未開放註冊時，register 意圖 fallback 開登入面板
export const resolveModalComponent = ({ intent, hasRegisterMethod } = {}) => {
  if (intent === MODAL_INTENT.LOGIN) return MODAL_BY_INTENT[MODAL_INTENT.LOGIN]
  if (intent === MODAL_INTENT.REGISTER) return hasRegisterMethod ? MODAL_BY_INTENT[MODAL_INTENT.REGISTER] : MODAL_BY_INTENT[MODAL_INTENT.LOGIN]
  return null
}

export const shouldOpenModalOnEntry = ({ intent, hasError, isLoggedIn } = {}) => Boolean(intent) && !hasError && !isLoggedIn

// ─────────────────────────────────────────────────────────────────────────────
// 反向同步（彈窗 → 網址）用的純函式：開窗改寫網址、關窗還原大廳網址
// 上面幾支是「網址 → 彈窗」，以下是反向；共用同一組路徑正規化與 guest scope 判定
// ─────────────────────────────────────────────────────────────────────────────

// 只有大廳與其登入／註冊子路由會反向改寫網址。白名單外（/main*、遊戲頁、未知路徑）
// 一律不同步：那些頁面的被動彈窗（ERR_A202 等）若改寫網址會把使用者拉離原本的頁面
const SYNCABLE_PATHS = ['/', '/login', '/register', GUEST_SCOPE, `${GUEST_SCOPE}/login`, `${GUEST_SCOPE}/register`]

export const isSyncablePath = (path, locales = []) => SYNCABLE_PATHS.includes(normalizeBasePath(path, locales))

// 「目前站在登入／註冊路徑上嗎」的單一述詞。與 resolveModalIntent() 刻意分開：
// 後者混了 ?error= 優先、/main* 守門這些與「路徑長相」無關的規則，拿它當述詞會讓
// 那些規則日後一改動，就連帶改掉本層 push/replace 與關窗的判定。
// 路徑清單直接由 PATH_INTENT_MAP 衍生，新增觸發路徑時兩邊不會分歧
const INTENT_PATHS = Object.keys(PATH_INTENT_MAP)

export const isIntentPath = (path, locales = []) => INTENT_PATHS.includes(normalizeBasePath(path, locales))

const isGuestScope = (basePath) => basePath === GUEST_SCOPE || basePath.startsWith(`${GUEST_SCOPE}/`)

// 語系前綴原樣回貼，不走 useLocalePath()：本專案 strategy 為 prefix_and_default，
// localePath 會把網址正規成 defaultLocale 形式，等於替使用者改了他自己打的網址
const localePrefixOf = (path, locales = []) => {
  const { locale } = stripLocalePrefix(path, locales)
  return locale ? `/${locale}` : ''
}

// 開窗時要導向的路徑：/guest 底下維持在 guest scope，其餘回大廳層級
export const buildIntentPath = ({ path, intent, locales = [] } = {}) => {
  if (intent !== MODAL_INTENT.LOGIN && intent !== MODAL_INTENT.REGISTER) return null

  const basePath = normalizeBasePath(path, locales)
  const scope = isGuestScope(basePath) ? GUEST_SCOPE : ''
  return `${localePrefixOf(path, locales)}${scope}/${intent}`
}

// 關窗時要還原的路徑：/guest/login 回 /guest（不是 /），其餘回 /
export const buildLobbyPath = ({ path, locales = [] } = {}) => {
  const basePath = normalizeBasePath(path, locales)
  const prefix = localePrefixOf(path, locales)
  if (isGuestScope(basePath)) return `${prefix}${GUEST_SCOPE}`
  // 無語系前綴時根路徑就是 '/'；有前綴時 '/th-TH' 才是該語系的大廳正規形式
  return prefix || '/'
}

// 彈窗名稱 → 意圖；非登入／註冊面板（ModalPasswordForget 等）回 null 代表不動網址
export const intentOfModalName = (name) => Object.keys(MODAL_BY_INTENT).find((intent) => MODAL_BY_INTENT[intent] === name) ?? null
